import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import React, { useEffect, useState, useCallback, useMemo } from "react";
import { DateTimePicker } from "@mui/x-date-pickers";
import { parse, isValid } from "date-fns";
import { useTranslation } from "react-i18next";
import { useAuth } from "../context/AuthContext";

const CustomDateTimePicke_01 = ({
  name,
  disabled,
  value,
  onChange,
  disabledOpenPicker,
  style,
  minDate,
  disableTime = false, // leaveType !== 1
}) => {
  const { i18n } = useTranslation();
  const { dateTimeFormat } = useAuth();

  const [selectedDate, setSelectedDate] = useState(null);

  // ===============================
  // Base format từ setting
  // ===============================
  const baseFormat = dateTimeFormat || "dd/MM/yyyy HH:mm";

  // ===============================
  // Bỏ phần time khỏi format khi disableTime
  // ===============================
  const removeTimeFromFormat = (fmt) =>
    typeof fmt === "string"
      ? fmt
        .replace(/\s*HH:mm:ss/, "")
        .replace(/\s*HH:mm/, "")
        .replace(/\s*HH/, "")
        .trim()
      : fmt;

  const displayFormat = useMemo(
    () => (disableTime ? removeTimeFromFormat(baseFormat) : baseFormat),
    [disableTime, baseFormat]
  );

  // ===============================
  // Parse value -> Date
  // ===============================
  const parseToDate = useCallback(
    (val) => {
      if (!val) return null;

      if (val instanceof Date && isValid(val)) return val;

      if (typeof val === "string") {
        const parsed = parse(val, baseFormat, new Date());
        return isValid(parsed) ? parsed : null;
      }

      return null;
    },
    [baseFormat]
  );

  // Sync từ cha
  useEffect(() => {
    setSelectedDate(parseToDate(value));
  }, [value, parseToDate]);

  // ===============================
  // Normalize ngày (31/02 -> 28/02)
  // ===============================
  const normalizeDate = (date) => {
    if (!(date instanceof Date) || !isValid(date)) return null;

    const y = date.getFullYear();
    const m = date.getMonth();
    const d = date.getDate();

    const lastDay = new Date(y, m + 1, 0).getDate();

    if (d > lastDay) {
      return new Date(
        y,
        m,
        lastDay,
        date.getHours(),
        date.getMinutes(),
        date.getSeconds()
      );
    }

    return date;
  };

  // ===============================
  // Handle change (typing / chọn)
  // ===============================
  const handleChange = (newValue) => {
    // Cho gõ mượt → chỉ set state tạm
    if (newValue && isValid(newValue)) {
      setSelectedDate(newValue);
    }
  };

  // ===============================
  // Handle accept / blur
  // ===============================
  const handleAccept = (newValue) => {
    if (!newValue || !isValid(newValue)) return;

    let finalValue = normalizeDate(newValue);

    // 🔒 disableTime → giữ nguyên giờ cũ
    if (disableTime && selectedDate) {
      finalValue = new Date(
        finalValue.getFullYear(),
        finalValue.getMonth(),
        finalValue.getDate(),
        selectedDate.getHours(),
        selectedDate.getMinutes(),
        selectedDate.getSeconds()
      );
    }

    setSelectedDate(finalValue);

    onChange?.({
      target: {
        name,
        value: finalValue,
      },
    });
  };

  // ===============================
  // Views
  // ===============================
  const views = disableTime
    ? ["year", "month", "day"]
    : ["year", "month", "day", "hours", "minutes"];

  // ===============================
  // Day of week
  // ===============================
  const dayOfWeekFormatter = (weekday) => {
    const isVI = i18n.language?.startsWith("vi");

    const map = isVI
      ? { Mon: "T2", Tue: "T3", Wed: "T4", Thu: "T5", Fri: "T6", Sat: "T7", Sun: "CN" }
      : { Mon: "Mo", Tue: "Tu", Wed: "We", Thu: "Th", Fri: "Fr", Sat: "Sa", Sun: "Su" };

    return map[String(weekday).slice(0, 3)] ?? weekday;
  };

  const normalizeFromString = (str) => {
    if (!str || typeof str !== "string") return null;

    const parsed = parse(str, displayFormat, new Date());

    if (!isValid(parsed)) {
      // fallback: parse ngày + tháng + năm thủ công
      const [d, m, y] = str.split("/").map(Number);
      if (!d || !m || !y) return null;

      const lastDay = new Date(y, m, 0).getDate();
      const safeDay = Math.min(d, lastDay);

      return new Date(
        y,
        m - 1,
        safeDay,
        selectedDate?.getHours() ?? 0,
        selectedDate?.getMinutes() ?? 0,
        selectedDate?.getSeconds() ?? 0
      );
    }

    return normalizeDate(parsed);
  };


  // ===============================
  // Render
  // ===============================
  return (
    <DateTimePicker
      value={selectedDate}
      format={displayFormat}
      views={views}
      ampm={false}
      disabled={disabled}
      minDate={minDate}
      closeOnSelect={false}
      dayOfWeekFormatter={dayOfWeekFormatter}
      onChange={handleChange}
      onAccept={handleAccept}
      slots={{ openPickerIcon: CalendarMonthIcon }}
      slotProps={{
        textField: {
          size: "small",
          fullWidth: true,
          inputProps: {
            readOnly: false,
            onBlur: (e) => {
              const normalized = normalizeFromString(e.target.value);
              if (!normalized) return;

              setSelectedDate(normalized);
              onChange?.({
                target: {
                  name,
                  value: normalized,
                },
              });
            },
          },
          sx: style,
        },
      }}
    />
  );
};

export default CustomDateTimePicke_01;
